import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { connectFirestoreEmulator, doc, getDoc, getFirestore } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getAnalytics } from "firebase/analytics";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

const isLocalhost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1" ||
  window.location.hostname === "[::1]";

const envFlag = (key: string): boolean => import.meta.env[key] === "true";

const useAllEmulators = envFlag("VITE_USE_EMULATOR");
const useAuthEmulator = useAllEmulators || envFlag("VITE_EMULATOR_AUTH");
const useFirestoreEmulator = useAllEmulators || envFlag("VITE_EMULATOR_FIRESTORE");
const useStorageEmulator = useAllEmulators || envFlag("VITE_EMULATOR_STORAGE");
const useFunctionsEmulator = useAllEmulators || envFlag("VITE_EMULATOR_FUNCTIONS");

const useAnyEmulator =
  isLocalhost &&
  (useAuthEmulator ||
    useFirestoreEmulator ||
    useStorageEmulator ||
    useFunctionsEmulator);

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const analytics = !isLocalhost ? getAnalytics(app) : null;

export const functions =
  useFunctionsEmulator && isLocalhost
    ? getFunctions(app)
    : getFunctions(app, import.meta.env.VITE_GCP_REGION);

if (useAnyEmulator) {
  const connectedEmulators: string[] = [];

  try {
    if (useAuthEmulator) {
      connectAuthEmulator(auth, "http://localhost:9099");
      connectedEmulators.push("auth:9099");
    }

    if (useFirestoreEmulator) {
      connectFirestoreEmulator(db, "localhost", 8088);
      connectedEmulators.push("firestore:8088");
    }

    if (useStorageEmulator) {
      connectStorageEmulator(storage, "localhost", 9199);
      connectedEmulators.push("storage:9199");
    }

    if (useFunctionsEmulator) {
      connectFunctionsEmulator(functions, "localhost", 5001);
      connectedEmulators.push("functions:5001");
    }

    console.log("Connecting to emulators:", connectedEmulators.join(", "));
  } catch (error) {
    console.error("Error connecting to emulators:", error);
    console.log("Emulators already connected");
  }
}

export const getFirestoreAccountId = async (): Promise<string | undefined> => {
  const user = auth.currentUser;
  if (!user) return undefined;

  // Force-refresh the token so recently assigned custom claims are available
  // before Firestore evaluates account membership rules.
  const { claims } = await user.getIdTokenResult(true);
  if (typeof claims.accountId === "string" && claims.accountId) {
    return claims.accountId;
  }

  // The user profile is the authoritative Firestore-to-account link. Custom
  // claims may be absent for older users, so retain the profile as a fallback.
  const userSnapshot = await getDoc(doc(db, "users", user.uid));
  const profileAccountId = userSnapshot.data()?.accountId;
  if (typeof profileAccountId === "string" && profileAccountId) {
    return profileAccountId;
  }

  return undefined;
};

export default app;
